import { AuthCallbackPage } from '@acme/app-shell'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

// ERP module (erp-api), shown in the menu only when the company has it. Every screen goes through
// ErpGate: it only mounts (and queries) with the module on and the permission (the API decides anyway).
const ErpGate = () => import('@/features/erp/shared/ErpGate.vue')
function erpScreen(path: string, name: string, page: () => Promise<unknown>, permission?: string, params: string[] = []): RouteRecordRaw {
  return {
    path,
    name,
    component: ErpGate,
    props: (route) => ({
      page,
      permission,
      ...Object.fromEntries(params.map((param) => [param, route.params[param]])),
    }),
  }
}

const erpScreens: RouteRecordRaw[] = [
  erpScreen('erp/painel', 'erp-dashboard', () => import('@/features/erp/dashboard/DashboardPage.vue')),
  // Clientes is the single CRM list since 10/10/2026; the old "Visão geral" address leads there.
  erpScreen('erp/clientes', 'erp-customers', () => import('@/features/erp/crm/CrmPage.vue'), 'crm.ver'),
  { path: 'erp/crm', redirect: (to) => ({ path: to.path.replace(/erp\/crm$/, 'erp/clientes'), query: to.query }) },
  erpScreen('erp/clientes/:customerId', 'erp-customer', () => import('@/features/erp/crm/CustomerPage.vue'), 'crm.ver', ['customerId']),
  erpScreen('erp/atividades', 'erp-activities', () => import('@/features/erp/crm/ActivitiesPage.vue'), 'crm.ver'),
  erpScreen('erp/historico', 'erp-history', () => import('@/features/erp/crm/CrmHistoryPage.vue'), 'crm.ver'),
  erpScreen('erp/automacoes', 'erp-automations', () => import('@/features/erp/crm/automation/AutomationsPage.vue'), 'crm.ver'),
  erpScreen('erp/projetos', 'erp-projects', () => import('@/features/erp/projects/ProjectsPage.vue'), 'projetos.ver'),
  erpScreen('erp/projetos/novo', 'erp-project-new', () => import('@/features/erp/projects/ProjectFormPage.vue'), 'projetos.editar'),
  erpScreen('erp/projetos/:projectId', 'erp-project', () => import('@/features/erp/projects/ProjectPage.vue'), 'projetos.ver', ['projectId']),
  erpScreen('erp/projetos/:projectId/editar', 'erp-project-edit', () => import('@/features/erp/projects/ProjectFormPage.vue'), 'projetos.editar', ['projectId']),
  // Relatório de acompanhamento do cliente (25/10/2026): review and send (also the link of the bell on the agreed day).
  erpScreen('erp/clientes/:customerId/relatorios/:reportId', 'erp-customer-report', () => import('@/features/erp/projects/CustomerReportPage.vue'), 'projetos.ver', [
    'customerId',
    'reportId',
  ]),
  erpScreen('erp/horas', 'erp-hours', () => import('@/features/erp/hours/HoursPage.vue'), 'horas.ver'),
  erpScreen('erp/escalas-de-trabalho', 'erp-work-schedules', () => import('@/features/erp/hours/WorkSchedulesPage.vue'), 'horas.todas'),
  // Diagnóstico do cliente (01/11/2026): one interview, its topics and the model questions.
  erpScreen('erp/diagnosticos/:diagnosisId', 'erp-diagnosis', () => import('@/features/erp/diagnoses/DiagnosisPage.vue'), 'projetos.ver', ['diagnosisId']),
  erpScreen('erp/tarefas', 'erp-tasks', () => import('@/features/erp/projects/TasksPage.vue'), 'projetos.ver'),
  erpScreen('erp/tarefas/nova', 'erp-task-new', () => import('@/features/erp/projects/TaskFormPage.vue'), 'projetos.editar'),
  erpScreen('erp/tarefas/:taskId', 'erp-task', () => import('@/features/erp/projects/TaskPage.vue'), 'projetos.ver', ['taskId']),
  erpScreen('erp/tarefas/:taskId/editar', 'erp-task-edit', () => import('@/features/erp/projects/TaskFormPage.vue'), 'projetos.editar', ['taskId']),
  erpScreen('erp/agenda', 'erp-calendar', () => import('@/features/erp/projects/CalendarPage.vue'), 'projetos.ver'),
  erpScreen('erp/reunioes', 'erp-meetings', () => import('@/features/erp/projects/MeetingsPage.vue'), 'projetos.ver'),
  erpScreen('erp/financeiro', 'erp-finance', () => import('@/features/erp/finance/FinancePage.vue'), 'financeiro.ver'),
  erpScreen('erp/contas-financeiras', 'erp-accounts', () => import('@/features/erp/finance/AccountsPage.vue'), 'financeiro.ver'),
  erpScreen('erp/categorias-financeiras', 'erp-financial-categories', () => import('@/features/erp/finance/FinancialCategoriesPage.vue'), 'financeiro.ver'),
  erpScreen('erp/regras-de-classificacao', 'erp-classification-rules', () => import('@/features/erp/finance/ClassificationRulesPage.vue'), 'financeiro.ver'),
  erpScreen('erp/modelos-de-projeto', 'erp-project-templates', () => import('@/features/erp/projects/ProjectTemplatesPage.vue'), 'projetos.ver'),
  erpScreen('erp/tipos-de-arquivo', 'erp-project-file-types', () => import('@/features/erp/projects/ProjectFileTypesPage.vue'), 'projetos.ver'),
  erpScreen('erp/tipos-de-atividade', 'erp-activity-types', () => import('@/features/erp/crm/ActivityTypesPage.vue'), 'crm.ver'),
  erpScreen('erp/contratos', 'erp-contracts', () => import('@/features/erp/contracts/ContractsPage.vue'), 'contratos.ver'),
  erpScreen('erp/contratos/:contractId', 'erp-contract', () => import('@/features/erp/contracts/ContractPage.vue'), 'contratos.ver', ['contractId']),
  erpScreen('erp/fornecedores', 'erp-suppliers', () => import('@/features/erp/finance/SuppliersPage.vue'), 'financeiro.ver'),
  erpScreen('erp/funcionarios', 'erp-employees', () => import('@/features/erp/finance/EmployeesPage.vue'), 'rh.ver'),
  erpScreen('erp/catalogo', 'erp-catalog', () => import('@/features/erp/commercial/CatalogPage.vue'), 'comercial.ver'),
  erpScreen('erp/propostas', 'erp-proposals', () => import('@/features/erp/commercial/ProposalsPage.vue'), 'comercial.ver'),
  erpScreen('erp/propostas/:proposalId', 'erp-proposal', () => import('@/features/erp/commercial/ProposalPage.vue'), 'comercial.ver', [
    'proposalId',
  ]),
  erpScreen('erp/chamados', 'erp-tickets', () => import('@/features/erp/support/TicketsPage.vue'), 'chamados.ver'),
  erpScreen('erp/chamados/instituicoes', 'erp-tickets-all', () => import('@/features/erp/support/MonitoredTicketsPage.vue'), 'chamados.monitorar'),
  erpScreen('erp/chamados/:ticketId', 'erp-ticket', () => import('@/features/erp/support/TicketPage.vue'), 'chamados.ver', ['ticketId']),
  erpScreen('erp/wiki', 'erp-wiki', () => import('@/features/erp/support/WikiPage.vue'), 'wiki.ver'),
  // Suporte: tickets with the platform's support, with or without the ERP ("suporte.abrir" counts without it).
  erpScreen('suporte', 'erp-support', () => import('@/features/support/SupportScreen.vue'), 'suporte.abrir'),
  erpScreen('suporte/:ticketId', 'erp-support-ticket', () => import('@/features/support/SupportScreen.vue'), 'suporte.abrir', ['ticketId']),
  // "Visão geral" left on 17/10/2026: its address opens the monthly report.
  { path: 'erp/relatorios', redirect: (to) => ({ path: `${to.path}/receitas-e-despesas`, query: to.query }) },
  erpScreen('erp/relatorios/projetos-e-tarefas', 'erp-projects-report', () => import('@/features/erp/reports/ProjectsReportPage.vue'), 'relatorios.ver'),
  // The person's own accounts (Google Agenda): no permission, every member with the ERP.
  erpScreen('erp/integracoes', 'erp-integrations', () => import('@/features/settings/IntegrationsPage.vue')),
  // The notes of Granola waiting for a customer (the person's own; connecting is in Integrações).
  erpScreen('erp/granola', 'erp-granola', () => import('@/features/erp/granola/GranolaNotesPage.vue'), 'projetos.ver'),
  erpScreen('erp/relatorios/comissoes', 'erp-commissions-report', () => import('@/features/erp/reports/CommissionsReportPage.vue'), 'relatorios.ver'),
  erpScreen('erp/relatorios/receitas-e-despesas', 'erp-finance-monthly', () => import('@/features/erp/reports/FinanceMonthlyPage.vue'), 'relatorios.ver'),
]

// Monitoring module (monitoring-api): alerts with scheduled queries. Same gate idea as the ERP's.
const MonitoringGate = () => import('@/features/monitoring/shared/MonitoringGate.vue')
function monitoringScreen(path: string, name: string, page: () => Promise<unknown>, configure = false, params: string[] = []): RouteRecordRaw {
  return {
    path,
    name,
    component: MonitoringGate,
    props: (route) => ({
      page,
      configure,
      ...Object.fromEntries(params.map((param) => [param, route.params[param]])),
    }),
  }
}

const monitoringScreens: RouteRecordRaw[] = [
  monitoringScreen('monitoramento', 'monitoring-alerts', () => import('@/features/monitoring/AlertsPage.vue')),
  monitoringScreen('monitoramento/novo', 'monitoring-alert-new', () => import('@/features/monitoring/AlertEditorPage.vue'), true),
  monitoringScreen('monitoramento/alertas/:alertId', 'monitoring-alert', () => import('@/features/monitoring/AlertPage.vue'), false, ['alertId']),
  monitoringScreen('monitoramento/alertas/:alertId/editar', 'monitoring-alert-edit', () => import('@/features/monitoring/AlertEditorPage.vue'), true, ['alertId']),
  // The page the alerts' messages link to ({address}/monitoramento/execucoes/{id}).
  monitoringScreen('monitoramento/execucoes/:runId', 'monitoring-run', () => import('@/features/monitoring/RunPage.vue'), false, ['runId']),
]

// CNPJ analysis module (monitoramento-cnpj-api): company network searches. Same gate idea as the ERP's.
const CnpjGate = () => import('@/features/cnpj/shared/CnpjGate.vue')
function cnpjScreen(path: string, name: string, page: () => Promise<unknown>, need?: 'search' | 'configure', params: string[] = []): RouteRecordRaw {
  return {
    path,
    name,
    component: CnpjGate,
    props: (route) => ({
      page,
      need,
      ...Object.fromEntries(params.map((param) => [param, route.params[param]])),
    }),
  }
}

const cnpjScreens: RouteRecordRaw[] = [
  cnpjScreen('analise-cnpj', 'cnpj-home', () => import('@/features/cnpj/SearchesPage.vue'), 'search'),
  cnpjScreen('analise-cnpj/pesquisas/:searchId', 'cnpj-search', () => import('@/features/cnpj/SearchPage.vue'), 'search', ['searchId']),
]

// Automação de Envios module (campaign-api): segments, content, campaigns, flows, contacts, results and exports.
const CampaignGate = () => import('@/features/campaign/shared/CampaignGate.vue')
function campaignScreen(path: string, name: string, page: () => Promise<unknown>, need?: 'edit' | 'export' | 'configure', params: string[] = []): RouteRecordRaw {
  return {
    path,
    name,
    component: CampaignGate,
    props: (route) => ({
      page,
      need,
      ...Object.fromEntries(params.map((param) => [param, route.params[param]])),
    }),
  }
}

const campaignScreens: RouteRecordRaw[] = [
  campaignScreen('envios', 'campaign-results', () => import('@/features/campaign/OverviewPage.vue')),
  // A campaign (what the API calls a flow) holds its steps, its segment and its contacts.
  campaignScreen('envios/campanhas', 'campaign-flows', () => import('@/features/campaign/FlowsPage.vue')),
  campaignScreen('envios/campanhas/novo', 'campaign-flow-new', () => import('@/features/campaign/FlowEditorPage.vue'), 'edit'),
  campaignScreen('envios/campanhas/:flowId', 'campaign-flow', () => import('@/features/campaign/FlowDetailPage.vue'), undefined, ['flowId']),
  campaignScreen('envios/campanhas/:flowId/editar', 'campaign-flow-edit', () => import('@/features/campaign/FlowEditorPage.vue'), 'edit', ['flowId']),
  campaignScreen('envios/conteudos', 'campaign-templates', () => import('@/features/campaign/TemplatesPage.vue')),
  campaignScreen('envios/conteudos/novo', 'campaign-template-new', () => import('@/features/campaign/TemplateEditorPage.vue'), 'edit'),
  campaignScreen('envios/conteudos/:templateId', 'campaign-template', () => import('@/features/campaign/TemplateEditorPage.vue'), undefined, ['templateId']),
  campaignScreen('envios/contatos', 'campaign-contacts', () => import('@/features/campaign/ContactsPage.vue')),
  campaignScreen('envios/contatos/:contactKey', 'campaign-contact', () => import('@/features/campaign/ContactDetailPage.vue'), undefined, ['contactKey']),
  campaignScreen('envios/descadastros', 'campaign-optouts', () => import('@/features/campaign/OptOutsPage.vue')),
  campaignScreen('envios/exportacoes', 'campaign-exports', () => import('@/features/campaign/ExportsPage.vue'), 'export'),
  campaignScreen('envios/configuracoes', 'campaign-settings', () => import('@/features/campaign/SettingsPage.vue'), 'configure'),
]

const tenantScreens: RouteRecordRaw[] = [
  { path: 'chat', name: 'chat', component: () => import('@/features/chat/ChatPage.vue'), meta: { bleed: true } },
  { path: 'conversas', name: 'conversations', component: () => import('@/features/conversations/ConversationsPage.vue') },
  { path: 'usuarios', name: 'users', component: () => import('@/features/tenant-users/UsersPage.vue') },
  { path: 'usuarios/:userId', name: 'user-detail', component: () => import('@/features/tenant-users/UserDetailPage.vue'), props: true },
  { path: 'papeis/:roleKey', name: 'role-edit', component: () => import('@/features/roles/RoleEditPage.vue'), props: true },
  { path: 'auditoria', name: 'audit', component: () => import('@/features/audit/AuditPage.vue') },
  { path: 'configuracoes', name: 'settings', component: () => import('@/features/settings/SettingsPage.vue') },
  ...erpScreens,
  ...monitoringScreens,
  ...cnpjScreens,
  ...campaignScreens,
]

// hostTenantId: tenant of the address being served (acme.aubaine.com.br). Its screens sit at the
// root of the host (/usuarios); the central host keeps /:tenantId/usuarios.
export function createPortalRouter(hostTenantId: string | null = null) {
  const common: RouteRecordRaw[] = [
    { path: '/auth/callback', component: AuthCallbackPage, meta: { public: true } },
    { path: '/empresas', name: 'tenant-picker', component: () => import('@/features/session/TenantPickerPage.vue') },
  ]

  // Página do cliente (chamados): no login, by the access link of the e-mails; only on the institution's address
  // (white label), where the links point.
  const customerRoutes: RouteRecordRaw[] = hostTenantId
    ? [
        {
          path: '/atendimento',
          component: () => import('@/features/customer/CustomerLayout.vue'),
          props: { tenantId: hostTenantId },
          meta: { public: true },
          children: [
            { path: '', name: 'customer-tickets', component: () => import('@/features/customer/CustomerTicketsPage.vue'), meta: { public: true } },
            {
              path: ':ticketId',
              name: 'customer-ticket',
              component: () => import('@/features/customer/CustomerTicketPage.vue'),
              props: true,
              meta: { public: true },
            },
          ],
        },
      ]
    : []

  // The supplier's page of a nota fiscal (no login, by the link of the request; only on the institution's address).
  const supplierRoutes: RouteRecordRaw[] = hostTenantId
    ? [
        {
          path: '/nota-fiscal',
          name: 'supplier-invoice',
          component: () => import('@/features/supplier/SupplierInvoicePage.vue'),
          props: { tenantId: hostTenantId },
          meta: { public: true },
        },
      ]
    : []

  const tenantRoutes: RouteRecordRaw[] = hostTenantId
    ? [
        {
          path: '/',
          component: () => import('@/pages/PortalLayout.vue'),
          props: { tenantId: hostTenantId },
          // The institution opens on the ERP dashboard (without the ERP, PortalLayout sends admins to the chat).
          children: [{ path: '', redirect: '/erp/painel' }, ...tenantScreens],
        },
      ]
    : [
        { path: '/', component: () => import('@/pages/HomeRedirectPage.vue') },
        {
          path: '/:tenantId',
          component: () => import('@/pages/PortalLayout.vue'),
          props: true,
          children: [{ path: '', redirect: (to) => `/${String(to.params.tenantId)}/erp/painel` }, ...tenantScreens],
        },
      ]

  return createRouter({
    // The app is built with base /assistente/ (its assets stay there); on a tenant address the
    // browser URLs start at the root and the proxy serves the app for any path.
    history: createWebHistory(hostTenantId ? '/' : import.meta.env.BASE_URL),
    routes: [...common, ...customerRoutes, ...supplierRoutes, ...tenantRoutes, { path: '/:pathMatch(.*)*', component: () => import('@/pages/NotFoundPage.vue') }],
  })
}
